import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, firebaseConfig } from '../../Firebaseconfig';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import * as XLSX from 'xlsx';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';

/* ── helpers ─────────────────────────────────────────────────────────────── */
function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
}

const GRAD = [
  ['#f97316','#ff6200'], ['#e56602','#f97316'], ['#ff8c00','#ff6200'],
  ['#f97316','#ef4444'], ['#8b5cf6','#d946ef'],
];
function avatarGrad(uid = '') {
  const [a, b] = GRAD[uid.charCodeAt(0) % GRAD.length];
  return `linear-gradient(135deg,${a},${b})`;
}

function roleBadge(role = '') {
  const r = role.toLowerCase();
  if (r === 'admin')    return 'badge badge-admin';
  if (r === 'merchant') return 'badge badge-merchant';
  if (r === 'courier')  return 'badge badge-courier';
  if (r === 'customer') return 'badge badge-customer';
  return 'badge badge-user';
}

function getComputedStatus(u) {
  if ((u.status || '').toLowerCase() === 'suspended') return 'Suspended';
  if (!u.lastLogin) return 'Inactive';
  const d = u.lastLogin.toDate ? u.lastLogin.toDate() : new Date(u.lastLogin);
  const diff = Date.now() - d.getTime();
  return diff < 5 * 60 * 1000 ? 'Active' : 'Inactive';
}

function fmtLastLogin(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`;
  if (hrs < 48)   return `${Math.floor(hrs/24)} day ago`;
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
}

const PER_PAGE = 10;

/* ── component ───────────────────────────────────────────────────────────── */
export default function AdminBar() {
  const [users,    setUsers]    = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', role: 'User' });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeMenuUserId, setActiveMenuUserId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const secondaryApp = getApps().find(app => app.name === 'SecondaryApp') 
        || initializeApp(firebaseConfig, 'SecondaryApp');
      const secondaryAuth = getAuth(secondaryApp);
      
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newUser.email, '12345678');
      const authUser = userCredential.user;
      
      await signOut(secondaryAuth);

      const userDoc = {
        uid: authUser.uid,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        role: newUser.role.toLowerCase(),
        status: 'Active',
        createdAt: new Date().toISOString()
      };
      await setDoc(doc(db, 'users', authUser.uid), userDoc);
      setUsers([userDoc, ...users]);
      setShowAddModal(false);
      setNewUser({ firstName: '', lastName: '', email: '', role: 'User' });
    } catch (err) {
      alert("Error adding user: " + err.message);
    }
  };

  const handleExport = () => {
    const dataToExport = filtered.map(u => ({
      ID: u.uid,
      'First Name': u.firstName || u.name || '',
      'Last Name': u.lastName || '',
      Email: u.email || '',
      Role: u.role || 'user',
      Status: getComputedStatus(u),
      'Last Login': u.lastLogin ? (u.lastLogin.toDate ? u.lastLogin.toDate().toLocaleString() : new Date(u.lastLogin).toLocaleString()) : '—',
      'Created At': u.createdAt ? (u.createdAt.toDate ? u.createdAt.toDate().toLocaleString() : new Date(u.createdAt).toLocaleString()) : '—'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "users_export.xlsx");
  };

  useEffect(() => {
    const closeMenu = () => setActiveMenuUserId(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const handleEditClick = (user) => {
    setEditingUser({
      uid: user.uid,
      firstName: user.firstName || user.name || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'Active'
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const userRef = doc(db, 'users', editingUser.uid);
      const updatedData = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        email: editingUser.email,
        role: editingUser.role.toLowerCase(),
        status: editingUser.status
      };
      await setDoc(userRef, updatedData, { merge: true });
      
      setUsers(users.map(u => u.uid === editingUser.uid ? { ...u, ...updatedData } : u));
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      alert("Error editing user: " + err.message);
    }
  };

  const handleDeleteClick = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.firstName || user.name || ''} ${user.lastName || ''}?`)) {
      try {
        await deleteDoc(doc(db, 'users', user.uid));
        setUsers(users.filter(u => u.uid !== user.uid));
      } catch (err) {
        alert("Error deleting user: " + err.message);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const q    = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        setUsers(data);
        setFiltered(data);
      } catch (e) {
        setError('Failed to load users. Check your Firestore rules.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let result = [...users];

    // 1. Search Filter
    const q = search.toLowerCase();
    if (q) {
      result = result.filter(u =>
        (u.name || u.firstName || '').toLowerCase().includes(q) ||
        (u.lastName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
      );
    }

    // 2. Sorting
    result.sort((a, b) => {
      let valA = '', valB = '';
      
      if (sortBy === 'name') {
        valA = ((a.firstName || a.name || '') + ' ' + (a.lastName || '')).toLowerCase().trim();
        valB = ((b.firstName || b.name || '') + ' ' + (b.lastName || '')).toLowerCase().trim();
      } else if (sortBy === 'date') {
        valA = a.createdAt || '';
        valB = b.createdAt || '';
      } else if (sortBy === 'role') {
        valA = (a.role || '').toLowerCase();
        valB = (b.role || '').toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFiltered(result);
    setPage(1);
  }, [search, users, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const rows       = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const pageNums = [];
  const start = Math.max(1, Math.min(page - 1, totalPages - 4));
  const end   = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) pageNums.push(i);

  const activeCount    = users.filter(u => getComputedStatus(u) === 'Active').length;
  const suspendedCount = users.filter(u => getComputedStatus(u) === 'Suspended').length;

  return (
    <div>
      {/* breadcrumb */}
      <div className="breadcrumb">
        <span className="bc-link">Home</span>
        <span className="bc-sep">›</span>
        <span className="bc-current">User Management</span>
      </div>

      {/* header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">● Total Users: <strong>{users.length.toLocaleString()}</strong></p>
        </div>
        <button className="btn-add-user" onClick={() => setShowAddModal(true)}>
          <span style={{fontSize:15}}>＋</span> Add New User
        </button>
      </div>

      {/* stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Active Now</div>
          <div className="stat-value">{activeCount}</div>
          <div className="stat-badge">↑ live</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{users.length.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Suspended</div>
          <div className="stat-value" style={{color:'var(--orange)'}}>{suspendedCount}</div>
        </div>
      </div>

      {/* toolbar */}
      <div className="table-toolbar">
        <div className="search-wrap">
          <span className="s-ico"><FaMagnifyingGlass /></span>
          <input
            type="text"
            placeholder="Search users by name, email or role…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        
        {/* Sort Dropdowns */}
        <select 
          className="btn-toolbar" 
          value={sortBy} 
          onChange={e => setSortBy(e.target.value)}
          style={{ appearance: 'none', paddingRight: '24px', backgroundImage: 'url("data:image/svg+xml;utf8,<svg fill=\'%239ca3af\' height=\'24\' viewBox=\'0 0 24 24\' width=\'24\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M7 10l5 5 5-5z\'/></svg>")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center' }}
        >
          <option value="name">Sort by Name</option>
          <option value="role">Sort by Role</option>
          <option value="date">Sort by Date</option>
        </select>

        <select 
          className="btn-toolbar" 
          value={sortOrder} 
          onChange={e => setSortOrder(e.target.value)}
          style={{ appearance: 'none', paddingRight: '24px', backgroundImage: 'url("data:image/svg+xml;utf8,<svg fill=\'%239ca3af\' height=\'24\' viewBox=\'0 0 24 24\' width=\'24\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M7 10l5 5 5-5z\'/></svg>")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center' }}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>

        <button className="btn-toolbar" onClick={handleExport}>↓ Export</button>
      </div>

      {/* table */}
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name / Avatar</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="loading-row">
                <td colSpan={6}><span className="spinner"/>Loading users…</td>
              </tr>
            ) : error ? (
              <tr className="loading-row">
                <td colSpan={6} style={{color:'var(--red)'}}>{error}</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={6}>No users found.</td>
              </tr>
            ) : rows.map(u => (
              <tr key={u.uid}>
                <td>
                  <div className="user-cell">
                    <div
                      className="user-avatar-circle"
                      style={{background: avatarGrad(u.uid)}}
                    >
                      {initials(u.name || u.email || '??')}
                    </div>
                    <div>
                      <div className="user-fullname">{u.firstName + ' ' + u.lastName || '—'}</div>
                      <div className="user-uid">ID: #{u.uid?.slice(0,5).toUpperCase()}</div>
                    </div>
                  </div>
                </td>
                <td>{u.email || '—'}</td>
                <td>
                  <span className={roleBadge(u.role)}>{u.role || 'User'}</span>
                </td>
                <td>
                  <span className={`status-pill ${getComputedStatus(u).toLowerCase()}`}>
                    <span className="status-dot-circle"/>
                    {getComputedStatus(u)}
                  </span>
                </td>
                <td style={{whiteSpace:'nowrap'}}>{fmtLastLogin(u.lastLogin)}</td>
                <td style={{ position: 'relative' }}>
                  <button 
                    className="action-menu-btn" 
                    title="More"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenuUserId(activeMenuUserId === u.uid ? null : u.uid);
                    }}
                  >
                    ⋮
                  </button>
                  {activeMenuUserId === u.uid && (
                    <div className="action-dropdown" style={{
                      position: 'absolute', right: '16px', top: '40px',
                      background: '#1c1c1c', border: '1px solid var(--border)',
                      borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                      zIndex: 10, display: 'flex', flexDirection: 'column', minWidth: '100px'
                    }}>
                      <button 
                        onClick={() => handleEditClick(u)}
                        style={{
                          background: 'none', border: 'none', color: '#fff',
                          padding: '8px 12px', textAlign: 'left', cursor: 'pointer',
                          fontSize: '12px', width: '100%', borderBottom: '1px solid var(--border)'
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(u)}
                        style={{
                          background: 'none', border: 'none', color: 'var(--red)',
                          padding: '8px 12px', textAlign: 'left', cursor: 'pointer',
                          fontSize: '12px', width: '100%'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* pagination */}
        {!loading && !error && filtered.length > 0 && (
          <div className="pagination-row">
            <span>
              Showing {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, filtered.length)} of{' '}
              {filtered.length.toLocaleString()} users
            </span>
            <div className="pagination-btns">
              <button className="pg-btn arrow" onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1}>‹</button>
              {pageNums.map(n => (
                <button key={n} className={`pg-btn ${n===page?'active':''}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              {end < totalPages && <span style={{color:'var(--text-muted)',padding:'0 3px',fontSize:12}}>…</span>}
              {end < totalPages && (
                <button className="pg-btn" onClick={() => setPage(totalPages)}>{totalPages}</button>
              )}
              <button className="pg-btn arrow" onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>›</button>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: '#181b22', padding: '24px', borderRadius: '12px', width: '400px',
            border: '1px solid #2e3340', boxShadow: '0 24px 64px rgba(0,0,0,0.55)', fontFamily: 'Inter, sans-serif'
          }}>
            <h2 style={{marginTop: 0, marginBottom: '20px', color: '#fff', fontSize: '20px', fontWeight: 600}}>Add New User</h2>
            <form onSubmit={handleAddUser} style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
              <input
                required placeholder="First Name" value={newUser.firstName}
                onChange={e => setNewUser({...newUser, firstName: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <input
                required placeholder="Last Name" value={newUser.lastName}
                onChange={e => setNewUser({...newUser, lastName: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <input
                required type="email" placeholder="Email" value={newUser.email}
                onChange={e => setNewUser({...newUser, email: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <select
                value={newUser.role}
                onChange={e => setNewUser({...newUser, role: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none', cursor: 'pointer'}}
              >
                <option value="User">User</option>
                <option value="Admin">Admin</option>
                <option value="Company">Company</option>
                <option value="Courier">Courier</option>
              </select>
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px'}}>
                <button type="button" onClick={() => setShowAddModal(false)} style={{
                  padding: '10px 20px', borderRadius: '50px', background: '#22262f', color: '#c0c5d0', border: '1.5px solid #2e3340', cursor: 'pointer', fontWeight: 600, fontSize: '13.5px'
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', borderRadius: '50px', background: 'linear-gradient(135deg, #fb923c 0%, #f97316 60%, #ea6c08 100%)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px', boxShadow: '0 4px 18px rgba(249,115,22,0.35)'
                }}>Add User</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditModal && editingUser && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: '#181b22', padding: '24px', borderRadius: '12px', width: '400px',
            border: '1px solid #2e3340', boxShadow: '0 24px 64px rgba(0,0,0,0.55)', fontFamily: 'Inter, sans-serif'
          }}>
            <h2 style={{marginTop: 0, marginBottom: '20px', color: '#fff', fontSize: '20px', fontWeight: 600}}>Edit User</h2>
            <form onSubmit={handleEditSubmit} style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
              <input
                required placeholder="First Name" value={editingUser.firstName}
                onChange={e => setEditingUser({...editingUser, firstName: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <input
                required placeholder="Last Name" value={editingUser.lastName}
                onChange={e => setEditingUser({...editingUser, lastName: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <input
                required type="email" placeholder="Email" value={editingUser.email}
                onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none'}}
              />
              <select
                value={editingUser.role}
                onChange={e => setEditingUser({...editingUser, role: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none', cursor: 'pointer'}}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="company">Company</option>
                <option value="courier">Courier</option>
              </select>
              <select
                value={editingUser.status}
                onChange={e => setEditingUser({...editingUser, status: e.target.value})}
                style={{padding: '12px', borderRadius: '8px', border: '1.5px solid #2e3340', background: '#1e2230', color: '#d0d4de', fontSize: '14px', outline: 'none', cursor: 'pointer'}}
              >
                <option value="Active">Active</option>
                <option value="Suspended">Suspended</option>
              </select>
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px'}}>
                <button type="button" onClick={() => { setShowEditModal(false); setEditingUser(null); }} style={{
                  padding: '10px 20px', borderRadius: '50px', background: '#22262f', color: '#c0c5d0', border: '1.5px solid #2e3340', cursor: 'pointer', fontWeight: 600, fontSize: '13.5px'
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', borderRadius: '50px', background: 'linear-gradient(135deg, #fb923c 0%, #f97316 60%, #ea6c08 100%)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px', boxShadow: '0 4px 18px rgba(249,115,22,0.35)'
                }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
